import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { useGuide } from "./guide-context";
import type { Guide } from "./guides";

/**
 * A guide, offered at the top of the card it belongs to.
 *
 * The guides aside lists everything for the step, which is the right place to browse and
 * the wrong place to be sent. A step whose whole job happens in somebody else's settings
 * screen leads with the instructions instead, in the same row treatment as the aside so
 * the two read as one thing.
 */
export const GuideRow = ({
  guide,
  title,
  detail,
}: {
  guide: Guide;
  title: string;
  detail: string;
}) => {
  const panel = useGuide();
  const open = panel?.open?.id === guide.id;

  return (
    <button
      type="button"
      onClick={() => panel?.setOpen(guide)}
      aria-current={open}
      className={cn(
        "flex w-full items-center gap-3 px-6 py-4 text-left transition-colors",
        {
          "bg-selected text-brand hover:bg-selected-hovered": open,
          "hover:bg-neutral-subtle-hovered": !open,
        },
      )}
    >
      <Stack align="start" gap={0} className="min-w-0 flex-1">
        <Text variant="label">{title}</Text>
        <Text variant="caption" tone="muted">
          {detail}
        </Text>
      </Stack>
      <CaretRightIcon
        className={cn("h-4 w-4 shrink-0", {
          "text-brand": open,
          "text-subtle": !open,
        })}
      />
    </button>
  );
};
